// Types owned by @kinnet-studio/pattern — re-exported verbatim so downstream
// code that imports from this module continues to work unchanged.
export type {
    GuideId,
    RingId,
    AnchorId,
    CircleRing,
    ArcRing,
    HandleType,
    BezierAnchor,
    LineGuide,
    ConcentricCirclesGuide,
    ConcentricArcsGuide,
    BezierCurveGuide,
    Guide,
    GuideCenterSymbol,
} from '@kinnet-studio/pattern';

export { GUIDE_CENTER_SYMBOLS } from '@kinnet-studio/pattern';

import type { Guide, ConcentricCirclesGuide, ConcentricArcsGuide } from '@kinnet-studio/pattern';

export const GUIDE_TYPES = ['line', 'concentric-circles', 'concentric-arcs', 'bezier-curve'] as const;
export type GuideType = (typeof GUIDE_TYPES)[number];

// Compile-time completeness check: if @kinnet-studio/pattern grows the Guide
// union, this Record fails to typecheck until the new type is added to
// GUIDE_TYPES above (and, symmetrically, a typo'd/removed entry fails the
// key type). Keeps the hand-maintained array honest against the package.
const GUIDE_TYPE_COVERED: Record<Guide['type'], GuideType> = {
    'line': 'line',
    'concentric-circles': 'concentric-circles',
    'concentric-arcs': 'concentric-arcs',
    'bezier-curve': 'bezier-curve',
};
void GUIDE_TYPE_COVERED;

export type GuideEndpoint = 'start' | 'end';

export type ConcentricGuide = ConcentricCirclesGuide | ConcentricArcsGuide;

/** Type guard for guides that have a `{ center, rings[] }` shape. Use this in
 *  place of `guide.type === 'concentric-circles'` whenever the branch only
 *  needs center+rings (it covers both arcs and circles). For arc-specific
 *  behavior (snap range, sliding clamp) keep the explicit type check. */
export function isConcentricGuide(g: Guide | undefined | null): g is ConcentricGuide {
    return g != null && (g.type === 'concentric-circles' || g.type === 'concentric-arcs');
}

/** Angle (radians, measured from +x axis) where the per-ring resize handle is
 *  rendered and hit-tested on a `concentric-circles` ring. Arcs derive their
 *  radial handle angle from `arcHandleMidAngle(ring)` instead. */
export const RING_HANDLE_ANGLE = 0;

export const TAU = Math.PI * 2;

/** Minimum sweep an arc ring may have, and equivalently the minimum gap. An arc
 *  ring's sweep is enforced to lie in `[MIN_ARC_SPAN, TAU - MIN_ARC_SPAN]` — a
 *  full circle is the concentric-circles type's job; a degenerate zero-sweep
 *  arc would be invisible. */
export const MIN_ARC_SPAN = 0.05;

/** Minimum world-space distance between adjacent rings of a concentric guide.
 *  Resizing a ring pushes its neighbors outward / inward to keep at least
 *  this gap; the cascade ripples through every neighbor in turn. Shared by the
 *  engine and the semantic guide ops so both enforce the same ordering. */
export const MIN_RING_GAP = 20;

/** Lower clamp on any ring radius — a 0-radius ring is degenerate (a point
 *  at the center can't really be snapped to). */
export const MIN_RING_RADIUS = 5;

/** Wrap an angle to `[0, 2π)`. */
export function mod2pi(x: number): number {
    return ((x % TAU) + TAU) % TAU;
}

/** Angle midway through the CCW arc span — used as the radial-resize handle
 *  position on arc rings. */
export function arcHandleMidAngle(startAngle: number, endAngle: number): number {
    return startAngle + mod2pi(endAngle - startAngle) / 2;
}

/** True if `angle` lies within the CCW arc span (inclusive endpoints). */
export function arcContains(startAngle: number, endAngle: number, angle: number): boolean {
    const sweep = mod2pi(endAngle - startAngle);
    const t = mod2pi(angle - startAngle);
    return t <= sweep + 1e-9;
}

/** Snap `angle` to the nearest arc endpoint when it lies outside `[start, end]`
 *  (CCW). When inside, returns `angle` unchanged. Ties (equidistant in the
 *  gap) resolve to `startAngle`. */
export function clampAngleToArc(startAngle: number, endAngle: number, angle: number): number {
    if (arcContains(startAngle, endAngle, angle)) return angle;
    const distFromEnd = mod2pi(angle - endAngle);
    const distToStart = mod2pi(startAngle - angle);
    return distFromEnd < distToStart ? endAngle : startAngle;
}

/** Clamp a moving arc endpoint's angle so the sweep with the fixed anchor stays
 *  in `[MIN_ARC_SPAN, TAU - MIN_ARC_SPAN]`. The returned angle may be outside
 *  `[0, TAU)`; arc helpers (`arcContains`, `arcHandleMidAngle`, etc.) handle
 *  un-normalized angles via `mod2pi`. Callers that need a normalized value
 *  for display or storage should apply `mod2pi` to the result. */
export function clampArcEndpoint(
    anchorAngle: number,
    requested: number,
    direction: 'forward' | 'backward',
): number {
    const min = MIN_ARC_SPAN;
    const max = TAU - MIN_ARC_SPAN;
    if (direction === 'forward') {
        const s = Math.max(min, Math.min(max, mod2pi(requested - anchorAngle)));
        return anchorAngle + s;
    }
    const s = Math.max(min, Math.min(max, mod2pi(anchorAngle - requested)));
    return anchorAngle - s;
}
